// Page: DoneForYou
// Purpose: Public landing page for DFY creative service with marquee showcase,
// features, pricing overview, and contact form posting to /contact.
import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import useDarkMode from '@/hooks/useDarkMode';
import SEOHead from '@/components/shared/SEOHead';
import Footer from '@/components/shared/Footer';
import { sendTextCalendlyUrl, gotvSiteUrl, textSupport } from '@/data/content/textSupportData';

// Images for the marquee (same as hero section)
const marqueeImages = [
  "/images/landing/hero1.png",
  "/images/landing/hero2.png",
  "/images/landing/hero3.png",
  "/images/landing/hero4.png",
  "/images/landing/hero5.png",
  "/images/landing/hero6.png",
  "/images/landing/hero7.png",
  "/images/landing/hero8.png",
  "/images/landing/hero9.png",
  "/images/landing/hero10.png",
  "/images/landing/hero11.png",
  "/images/landing/hero12.png",
  "/images/landing/hero13.png",
  "/images/landing/hero14.png",
  "/images/landing/hero15.png",
  "/images/landing/hero16.png",
  "/images/landing/hero17.png",
  "/images/landing/hero18.png",
  "/images/landing/hero19.png",
];

// Split the images array into two rows
const firstRow = marqueeImages.slice(0, Math.ceil(marqueeImages.length / 2));
const secondRow = marqueeImages.slice(Math.ceil(marqueeImages.length / 2));

// Template Card component
const TemplateCard = ({ image }) => {
  return (
    <figure className="relative h-48 w-48 sm:h-64 sm:w-64 mx-2 sm:mx-3 cursor-pointer overflow-hidden rounded-xl border border-gray-950/[.1] bg-gray-950/[.01] hover:bg-gray-950/[.05] dark:border-gray-50/[.1] dark:bg-gray-50/[.10] dark:hover:bg-gray-50/[.15]">
      <img 
        src={image} 
        alt="Template example" 
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </figure>
  );
};

// Simple Marquee component
const Marquee = ({ children, reverse = false, className = "" }) => {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <motion.div
        initial={{ x: reverse ? "-100%" : 0 }}
        animate={{ x: reverse ? 0 : "-100%" }}
        transition={{
          duration: 30,
          repeat: Infinity,
          repeatType: "loop",
          ease: "linear",
        }}
        className="flex whitespace-nowrap"
        style={{
          transform: 'translateZ(0)',
          willChange: 'transform'
        }}
      >
        <div className="flex">{children}</div>
        <div className="flex">{children}</div>
      </motion.div>
    </div>
  );
};

// Utility function for scrolling to sections - removed to fix scroll animation issue

export default function DoneForYou() {
  const isDark = useDarkMode();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleTheme = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <SEOHead pageType="done-for-you-landing" />
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        backLink="/"
        showLogo={true}
      />
      
      {/* Hero section with Marquee */}
      <section className="bg-white dark:bg-gray-900 py-12 sm:py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl md:text-center xl:max-w-none mb-12">
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400 text-center">Done For You</h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight mx-auto text-center max-w-[90%]">
              Full-Service Campaign Execution
            </p>
            <p className="mt-6 text-lg text-gray-600 dark:text-gray-300 mx-auto text-center max-w-[90%]">
              Creative, texting, voice, and data—all handled by experts so you can focus on winning.
            </p>
          </div>
          
          {/* Marquee Section */}
          <div className="relative w-full overflow-hidden">
            <Marquee className="mb-6">
              {firstRow.map((image, index) => (
                <TemplateCard key={`first-${index}`} image={image} />
              ))}
            </Marquee>
            
            <Marquee reverse className="mb-6">
              {secondRow.map((image, index) => (
                <TemplateCard key={`second-${index}`} image={image} />
              ))}
            </Marquee>
            
            {/* Gradient fades at the edges */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-1/6 bg-gradient-to-r from-white dark:from-gray-900 z-20"></div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-1/6 bg-gradient-to-l from-white dark:from-gray-900 z-20"></div>
          </div>
          
          {/* Disclaimer */}
          <div className="mx-auto mt-8 mb-8 max-w-4xl px-6 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Templates based on publicly available designs used in real political campaigns and advertisements. The inclusion of these designs does not imply any affiliation, partnership, or connection with the original campaign, candidate, or organization. It does not constitute an endorsement of our services by them, nor does it grant any right or license to use their original branding, images, or other copyrighted materials, which remain the property of their respective owners.
            </p>
          </div>
          
          {/* Book a Call Button */}
          <div className="mt-12 text-center">
            <a
              href={sendTextCalendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold text-sm sm:text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
            >
              <Calendar className="mr-2 h-5 w-5" />
              Book a Call
            </a>
          </div>
        </div>
      </section>
      
      {/* Core Services - 6 Main Categories */}
      <section className="bg-white dark:bg-gray-900 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400 text-center">Full-Service Campaign Solutions</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              We handle everything end-to-end
            </p>
            <p className="mt-6 text-lg text-gray-600 dark:text-gray-300 text-center">
              Data-driven voter outreach, FCC-compliant texting, and creative built for results.
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {/* Texting */}
            <div className="relative group">
              <div className="h-full p-8 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-200 dark:hover:border-purple-800">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89-3.26a2 2 0 011.58 0L21 8m-9 4l9-4M3 8l9 4m0 0v8" /></svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Texting</h3>
                </div>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>FCC-compliant P2P SMS with 10DLC registration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>HD pictures & video (MMS) support</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Two-way conversations with 14-day messaging</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Real-time analytics & CTR tracking</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Managed sending & scheduling options</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Voice */}
            <div className="relative group">
              <div className="h-full p-8 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-200 dark:hover:border-purple-800">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h2l3 7v5a2 2 0 002 2h6a2 2 0 002-2v-5l3-7h2" /></svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Voice</h3>
                </div>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>RoboCalls to landlines with voicemail</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Live transfer & keypad surveys</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Instant voter polling & feedback</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Professional voice talent available</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Automated scheduling & delivery</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Voter Data */}
            <div className="relative group">
              <div className="h-full p-8 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-200 dark:hover:border-purple-800">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h7m0 0V4m0 3v3m11-3h-7m0 0V4m0 3v3M5 21h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Voter Data</h3>
                </div>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Updated voter files & targeting</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Landline, mobile, & email lookups</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Demographic & carrier appends</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Custom list uploads & hygiene</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Segmentation & micro-targeting</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Creative & Design */}
            <div className="relative group">
              <div className="h-full p-8 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-200 dark:hover:border-purple-800">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3a1 1 0 011 1v2l2 2-2 2v2l-2 2 2 2v2a1 1 0 01-1 1H7l-4 4v-6H3a1 1 0 001-1v-2l2-2-2-2V8a1 1 0 011-1h4l4-4z" /></svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Creative & Design</h3>
                </div>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Campaign concepting & strategy</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Ad copywriting that converts</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Multi-format deliverables (9:16, 1:1, 4:5)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Ready for Meta, Google, & programmatic</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Unlimited revisions & fast turnaround</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Compliance & Support */}
            <div className="relative group">
              <div className="h-full p-8 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-200 dark:hover:border-purple-800">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Compliance & Support</h3>
                </div>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>FCC-compliant practices & opt-out handling</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Strict list hygiene & responsible use</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Dedicated account support</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Priority onboarding & training</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Proactive guidance from political experts</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Web & Hosting */}
            <div className="relative group">
              <div className="h-full p-8 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-200 dark:hover:border-purple-800">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Web & Hosting</h3>
                </div>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>10DLC registration & carrier approvals</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Website hosting & domain management</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Setup fees waived on higher tiers</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Technical infrastructure support</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Expedited setup & deployment</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      
      {/* Results highlights (minimal social proof) */}
      <section className="bg-white dark:bg-gray-900 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400 text-center">Proven Impact</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">Trusted by thousands of winning campaigns</p>
          </div>
          
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-1 lg:grid-cols-3">
            {/* Custom Output Plans */}
            <div className="relative group">
              <div className="h-full p-8 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-200 dark:hover:border-purple-800">
                <div className="text-center">
                  <div className="flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-xl mx-auto mb-6">
                    <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data-Driven Strategy</h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">Battle-tested outreach strategies tailored to your district and race.</p>
                </div>
              </div>
            </div>

            {/* Flexible Workflow */}
            <div className="relative group">
              <div className="h-full p-8 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-200 dark:hover:border-purple-800">
                <div className="text-center">
                  <div className="flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-xl mx-auto mb-6">
                    <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Cost-Effective at Scale</h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">Reach voters for pennies per contact with transparent metered pricing.</p>
                </div>
              </div>
            </div>

            {/* Built for Agencies & Teams */}
            <div className="relative group">
              <div className="h-full p-8 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-200 dark:hover:border-purple-800">
                <div className="text-center">
                  <div className="flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-xl mx-auto mb-6">
                    <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Expert Support</h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">Award-winning client support with real humans and fast turnarounds.</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* CTA Button */}
          <div className="mt-12 text-center">
            <a
              href={sendTextCalendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-3 rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
            >
              <Calendar className="mr-2 h-5 w-5" />
              Book a Call
            </a>
            <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              Prefer to call? Speak with a real person: <a href={`tel:${textSupport.phoneNumberHref}`} className="underline decoration-purple-400 decoration-2 underline-offset-2 hover:text-purple-700 dark:hover:text-purple-300">{textSupport.phoneNumberDisplay}</a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact section replaced by CTA above to avoid duplication */}
      
      <Footer />
    </div>
  );
}